import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Lock, Truck, Scale, ShieldCheck, BadgeCheck, MapPin,
  Handshake, Wallet, CheckCircle2, Package,
} from 'lucide-react';
import TrustTradeLogo from '../components/TrustTradeLogo';
import { trackSignUpClick } from '../utils/analytics';
import { VDark } from '../components/designTokens';

// Launch default is OFF — Courier Guy has not completed real end-to-end
// certification yet. Must match the backend/other-page default exactly so
// marketing copy never promises a feature that isn't actually available.
const COURIER_ENABLED = process.env.REACT_APP_COURIER_ENABLED === 'true';

// Re-exposed under this file's existing field names so every C.xxx reference
// below keeps working, but every value now comes from the one shared dark
// palette (designTokens.js VDark) instead of a separate local one.
const C = {
  bg: VDark.bg,
  card: VDark.surfaceSolid,
  cardHover: VDark.hover,
  border: VDark.border,
  text: VDark.text,
  muted: VDark.sub,
  dim: VDark.dim,
  accent: VDark.accent,
  success: VDark.success,
};

const MISSION = [
  {
    icon: Lock,
    emoji: '🔒',
    title: 'Protected payments',
    body: 'Your payment is protected until you confirm receipt — never released to the other party before you are happy.',
  },
  {
    icon: Truck,
    emoji: '🚚',
    title: COURIER_ENABLED ? 'Integrated Courier' : 'Integrated Courier (coming soon)',
    body: COURIER_ENABLED
      ? 'We book Courier Guy automatically on your behalf, with live tracking from collection to your door.'
      : 'Arrange delivery in person or digitally today — automatic Courier Guy booking with live tracking is coming soon.',
  },
  {
    icon: Scale,
    emoji: '⚖️',
    title: 'Fair Disputes',
    body: 'If something goes wrong, our AI analyses the evidence and gives our team a recommendation. A human admin always reviews the case and makes the final call on any fund release.',
  },
];

const STATS = [
  { icon: ShieldCheck, title: 'Protected payments', sub: 'Third-party payment provider' },
  { icon: BadgeCheck, title: '256-bit encryption', sub: 'Bank-grade protected payments' },
  { icon: MapPin, title: 'Proudly South African', sub: 'Built and operated locally' },
];

const STEPS = [
  {
    icon: Handshake,
    emoji: '🤝',
    title: 'Agree on terms',
    body: 'Both parties confirm the transaction details and price.',
  },
  {
    icon: Wallet,
    emoji: '💰',
    title: 'Buyer pays securely',
    body: 'Payments are processed and securely held by our third-party payment provider until the transaction conditions are met.',
  },
  {
    icon: CheckCircle2,
    emoji: '✅',
    title: 'Seller delivers, buyer confirms',
    body: 'Funds release only when the buyer confirms receipt.',
  },
];

const FAQS = [
  {
    q: 'Is my money safe?',
    a: 'Yes. Payments are processed and securely held by our third-party payment provider until the transaction conditions are met. TrustTrade does not hold customer funds.',
  },
  {
    q: 'What does it cost?',
    a: 'TrustTrade charges a platform fee per transaction. Payment-provider processing fees depend on the payment method you choose. The applicable amounts are shown before you pay.',
  },
  {
    q: 'What if something goes wrong?',
    a: 'Raise a dispute. Our AI analyses the evidence submitted by both parties and gives our team a recommendation, but a human admin always reviews the case and makes the final decision on any fund release.',
  },
  {
    q: 'How long does payout take?',
    a: 'Same day or next business day after the buyer confirms receipt.',
  },
];

const TRUST_BADGES = [
  { icon: ShieldCheck, label: 'TrustTrade Protection', sub: 'Protected payments' },
  // Only advertise the Courier Guy partnership once courier booking is actually enabled.
  ...(COURIER_ENABLED ? [{ icon: Package, label: 'Courier Guy', sub: 'Delivery partner' }] : []),
];

export default function AboutPage() {
  return (
    <div className="about-page" style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" }}>
      <style>{`
        /* The global stylesheet sets all h1–h6 to a dark colour, which is unreadable
           on this dark page. Force every heading inside the About page to light text;
           the muted section labels keep their own inline colour (inline beats this). */
        .about-page h1, .about-page h2, .about-page h3,
        .about-page h4, .about-page h5, .about-page h6 { color: ${C.text}; }
        .about-wrap { max-width: 980px; margin: 0 auto; padding: 0 20px; }
        .about-mission-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
        .about-stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
        .about-steps-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
        .about-badge-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
        .about-founder { display: grid; grid-template-columns: 120px 1fr; gap: 28px; align-items: start; }
        .about-card { transition: border-color .15s ease, background .15s ease, transform .15s ease; }
        .about-card:hover { border-color: #3d4757; background: ${C.cardHover}; transform: translateY(-2px); }
        @media (max-width: 760px) {
          .about-mission-grid { grid-template-columns: 1fr; }
          .about-stats-grid { grid-template-columns: 1fr; }
          .about-steps-grid { grid-template-columns: 1fr; }
          .about-badge-grid { grid-template-columns: 1fr; }
          .about-founder { grid-template-columns: 1fr; gap: 18px; justify-items: center; text-align: center; }
          .about-hero-h1 { font-size: 34px !important; }
        }
      `}</style>

      {/* Nav */}
      <nav style={{ borderBottom: `1px solid ${C.border}`, background: 'rgba(13,17,23,0.85)', backdropFilter: 'blur(8px)', position: 'sticky', top: 0, zIndex: 50 }}>
        <div className="about-wrap" style={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: C.muted, textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>
            <ArrowLeft size={16} /> Back to Home
          </Link>
          <TrustTradeLogo dark size="small" showText />
        </div>
      </nav>

      {/* Hero */}
      <section className="about-wrap" style={{ paddingTop: 72, paddingBottom: 48, textAlign: 'center' }}>
        <span style={{ display: 'inline-block', fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.accent, background: 'rgba(47,155,255,0.12)', border: '1px solid rgba(47,155,255,0.25)', borderRadius: 999, padding: '6px 14px', marginBottom: 24 }}>
          Our Story
        </span>
        <h1 className="about-hero-h1" style={{ fontSize: 48, fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.02em', margin: '0 auto 18px', maxWidth: 760 }}>
          Making online buying and selling safer in South Africa
        </h1>
        <p style={{ fontSize: 19, color: C.muted, lineHeight: 1.6, margin: '0 auto', maxWidth: 560 }}>
          A personal mission to end marketplace scams.
        </p>
      </section>

      {/* Founder story */}
      <section className="about-wrap" style={{ paddingBottom: 56 }}>
        <div className="about-card about-founder" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 18, padding: '34px 32px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 104, height: 104, borderRadius: '50%', flexShrink: 0,
              background: VDark.accentGradient,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 36, fontWeight: 800, color: '#061421', letterSpacing: '0.02em',
              boxShadow: VDark.shadow,
            }}>
              MR
            </div>
          </div>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 2px' }}>Marnich Roets</h2>
            <p style={{ fontSize: 14, fontWeight: 600, color: C.accent, margin: '0 0 20px' }}>Founder, TrustTrade</p>

            <div style={{ fontSize: 15.5, lineHeight: 1.75, color: '#C9D4E0' }}>
              <p style={{ margin: '0 0 16px' }}>
                In 2025, I lost R10,000 in an online marketplace scam.
              </p>
              <p style={{ margin: '0 0 16px' }}>
                Instead of accepting it as part of buying and selling online, I decided to build a solution.
              </p>
              <p style={{ margin: '0 0 16px' }}>
                TrustTrade was created to help South Africans buy and sell with confidence with
                protected payments until delivery or handover is confirmed.
              </p>
              <p style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.text }}>
                Simple, secure and built for South Africa.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="about-wrap" style={{ paddingBottom: 56 }}>
        <div className="about-card" style={{ background: 'linear-gradient(135deg, rgba(47,155,255,0.12) 0%, rgba(61,209,148,0.07) 100%)', border: `1px solid ${C.border}`, borderRadius: 18, padding: '34px 32px' }}>
          <p style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.accent, margin: '0 0 14px' }}>
            Our Vision
          </p>
          <p style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.35, color: C.text, margin: '0 0 18px', maxWidth: 780 }}>
            Our vision is to become South Africa&rsquo;s trusted payment layer for online deals between buyers and sellers.
          </p>
          <p style={{ fontSize: 15.5, lineHeight: 1.75, color: '#C9D4E0', margin: '0 0 16px', maxWidth: 820 }}>
            TrustTrade is not here to replace marketplaces. Whether a deal starts on Facebook Marketplace, WhatsApp, Gumtree, Instagram or anywhere else, the payment is securely held by our third-party payment provider until the transaction conditions are met.
          </p>
          <p style={{ fontSize: 17, fontWeight: 800, color: C.text, margin: 0 }}>
            Not another marketplace &mdash; a safer way to complete the deal.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="about-wrap" style={{ paddingBottom: 56 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.muted, textAlign: 'center', margin: '0 0 24px' }}>
          What we do
        </h2>
        <div className="about-mission-grid">
          {MISSION.map(({ icon: Icon, emoji, title, body }) => (
            <div key={title} className="about-card" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '26px 22px' }}>
              <div style={{ width: 46, height: 46, borderRadius: 12, background: 'rgba(47,155,255,0.12)', border: '1px solid rgba(47,155,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                <Icon size={22} color={C.accent} />
              </div>
              <h3 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 8px' }}>
                <span style={{ marginRight: 7 }}>{emoji}</span>{title}
              </h3>
              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.6, margin: 0 }}>{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="about-wrap" style={{ paddingBottom: 48 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.muted, textAlign: 'center', margin: '0 0 24px' }}>
          How it works
        </h2>
        <div className="about-steps-grid">
          {STEPS.map(({ icon: Icon, emoji, title, body }, i) => (
            <div key={title} className="about-card" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '26px 22px', position: 'relative' }}>
              <span style={{ position: 'absolute', top: 18, right: 20, fontSize: 13, fontWeight: 800, color: C.dim }}>0{i + 1}</span>
              <div style={{ width: 46, height: 46, borderRadius: 12, background: 'rgba(47,155,255,0.12)', border: '1px solid rgba(47,155,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                <Icon size={22} color={C.accent} />
              </div>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 8px' }}>
                <span style={{ marginRight: 7 }}>{emoji}</span>{title}
              </h3>
              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.6, margin: 0 }}>{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Social proof banner */}
      <section className="about-wrap" style={{ paddingBottom: 56 }}>
        <div style={{
          background: 'rgba(47,155,255,0.08)', border: `1px solid rgba(47,155,255,0.22)`,
          borderRadius: 16, padding: '24px 28px', textAlign: 'center',
        }}>
          <p style={{ fontSize: 16.5, fontWeight: 600, color: C.text, lineHeight: 1.55, margin: 0 }}>
            Trusted by South Africans buying and selling everything from electronics to vehicles to freelance services.
          </p>
        </div>
      </section>

      {/* Stats / trust */}
      <section className="about-wrap" style={{ paddingBottom: 56 }}>
        <div className="about-stats-grid">
          {STATS.map(({ icon: Icon, title, sub }) => (
            <div key={title} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '22px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(61,209,148,0.12)', border: '1px solid rgba(61,209,148,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={20} color={C.success} />
              </div>
              <div>
                <p style={{ fontSize: 15, fontWeight: 700, margin: '0 0 2px' }}>{title}</p>
                <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>{sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Trust badges */}
      <section className="about-wrap" style={{ paddingBottom: 56 }}>
        <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.dim, textAlign: 'center', margin: '0 0 18px' }}>
          Backed by trusted partners
        </p>
        <div className="about-badge-grid">
          {TRUST_BADGES.map(({ icon: Icon, label, sub }) => (
            <div key={label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 8 }}>
              <div style={{ width: 44, height: 44, borderRadius: 11, background: 'rgba(230,237,243,0.06)', border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon size={22} color={C.text} />
              </div>
              <p style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{label}</p>
              <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>{sub}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="about-wrap" style={{ paddingBottom: 56 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.muted, textAlign: 'center', margin: '0 0 24px' }}>
          Frequently asked questions
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {FAQS.map(({ q, a }) => (
            <div key={q} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '20px 22px' }}>
              <p style={{ fontSize: 15.5, fontWeight: 700, margin: '0 0 7px', color: C.text }}>{q}</p>
              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.65, margin: 0 }}>{a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="about-wrap" style={{ paddingBottom: 80 }}>
        <div style={{
          background: 'linear-gradient(135deg, rgba(47,155,255,0.14) 0%, rgba(61,209,148,0.10) 100%)',
          border: `1px solid ${C.border}`, borderRadius: 20, padding: '44px 28px', textAlign: 'center',
        }}>
          <h2 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.01em', margin: '0 0 10px' }}>
            Start your first secure transaction
          </h2>
          <p style={{ fontSize: 16, color: C.muted, margin: '0 0 26px' }}>It takes 2 minutes.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
            <Link
              to="/login"
              onClick={() => trackSignUpClick({ source: 'about_cta' })}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 9,
                background: C.accent, color: '#061421', textDecoration: 'none',
                fontSize: 15, fontWeight: 700, padding: '14px 28px', borderRadius: 11,
                boxShadow: VDark.shadow,
              }}
            >
              Get Started Free <ArrowRight size={17} />
            </Link>
            <Link
              to="/demo"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 9,
                background: 'transparent', color: C.text, textDecoration: 'none',
                fontSize: 15, fontWeight: 700, padding: '14px 28px', borderRadius: 11,
                border: `1px solid ${C.border}`,
              }}
            >
              Try the demo <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
